/* eslint-disable camelcase */
import Cookies from 'js-cookie'
import { App, computed } from 'vue'
import { createI18n } from 'vue-i18n'
import enLocal from './en'
import zhLocal from './zh'
import frLocal from './fr'
import itLocal from './it'
import nlLocal from './nl'
import router from '@/router/index'
import store from '@/store/index'
import { MicroAppCacheKey } from '@/enums/microAppCacheKey'
import { isPublicRoute } from '@/config/publicRoutes'

export const langKey = 'lang'

export function isI18nDevMode(): boolean {
  return localStorage.getItem(MicroAppCacheKey.DevelopModeKey) === 'true'
}

export function setI18nDevMode(enabled: boolean) {
  if (enabled) {
    localStorage.setItem(MicroAppCacheKey.DevelopModeKey, true as any)
  } else {
    localStorage.removeItem(MicroAppCacheKey.DevelopModeKey)
  }
}

export const langKeyMap = {
  zh: 'zh',
  en: 'en',
  fr: 'fr',
  nl: 'nl',
  it: 'it'
}

export const langTextMap = {
  [langKeyMap.zh]: '简体中文',
  [langKeyMap.en]: 'English',
  [langKeyMap.fr]: 'Français',
  [langKeyMap.nl]: 'Nederlands',
  [langKeyMap.it]: 'Italia'
}

type LangMapKey = keyof typeof langTextMap

type Message = Record<LangMapKey, {[key: string]: any}>

const messages = {
  [langKeyMap.en]: enLocal,
  [langKeyMap.zh]: zhLocal,
  [langKeyMap.fr]: frLocal,
  [langKeyMap.it]: itLocal,
  [langKeyMap.nl]: nlLocal
}

function getDefaultLanguage(validLocales: string[]): string {
  // 默认优先级：系统语言 -> window.VUE_APP_AREA -> en
  const browserLanguage = (navigator.language || navigator.browserLanguage || '')
    .substring(0, 2)
    .toLowerCase()
  if (browserLanguage && validLocales.includes(browserLanguage)) {
    return browserLanguage
  }

  const areaLanguage = (window.VUE_APP_AREA || '').toLowerCase()
  if (areaLanguage && validLocales.includes(areaLanguage)) {
    return areaLanguage
  }

  return langKeyMap.en
}

export const i18n: any = createI18n({
  locale: getLanguage(), // set locale
  // 缺词条时的补充语言，也按相同优先级
  fallbackLocale: getDefaultLanguage(Object.keys(messages)),
  messages, // set locale messages
  silentTranslationWarn: true,
  legacy: false,
  missingWarn: false,
  fallbackWarn: false
})

// 持久化语言选择
function persistLanguage(val: string) {
  Cookies.set(langKey, val)
  localStorage.setItem(langKey, val)
}

export function getLanguage() {
  const chooseLanguage = localStorage.getItem(langKey) || Cookies.get(langKey)
  const validLocales = Object.keys(messages)
  if (chooseLanguage && validLocales.includes(chooseLanguage)) {
    if (localStorage.getItem(langKey) !== chooseLanguage) {
      localStorage.setItem(langKey, chooseLanguage)
    }
    if (Cookies.get(langKey) !== chooseLanguage) {
      Cookies.set(langKey, chooseLanguage)
    }
    return chooseLanguage
  }
  if (chooseLanguage && !validLocales.includes(chooseLanguage)) {
    Cookies.remove(langKey)
    localStorage.removeItem(langKey)
  }

  // 未手动设置时，按默认优先级自动选择并持久化
  const defaultLanguage = getDefaultLanguage(validLocales)
  persistLanguage(defaultLanguage)
  return defaultLanguage
}

export interface SetLanguageOptions {
  /** 仅持久化不更新 locale，用于切换后 reload 前避免词条闪烁 */
  persistOnly?: boolean
}

export function setLanguage(val: LangMapKey, options?: SetLanguageOptions) {
  if (!options?.persistOnly) {
    i18n.global.locale.value = val
  }
  Cookies.set(langKey, val as string)
  localStorage.setItem(langKey, val as string)
  window.postMessage(
    {
      type: 'changeLanguage',
      data: {
        lang: val
      }
    },
    window.origin
  )
}

export const $t = (key: string, ...args: any[]) => {
  if (key == null || key === '') return ''
  // 开发者模式：展示词条 key；词条模式：展示翻译
  if (isI18nDevMode()) return key
  return i18n.global.t(key, ...args) || key
}

export function installI18n(app: App<Element>) {
  app.use(i18n)
  app.provide('$t', $t)
  app.config.globalProperties.$t = $t
}

/**
 * 设置页面标题，逻辑根据router/index 内同步
 */
export function setDocumentTitle() {
  const to: any = router.currentRoute.value
  if (to.meta?.title) {
    document.title = to.meta.title
  }
  if (to.path === '/login') {
    document.title = $t('main_login_btn_text')
  } else {
    if (to.path === '/') {
      document.title = $t('main_home_home_name')
    } else {
      const state: any = store.state
      const pageList: [] = state.menu.pageList
      const title: string = to.query?.title || ''
      const publicRoute = isPublicRoute(to.path)
      const matchedRoute: any = pageList.find((page: any) => {
        return to.path === page.routeUrl
      })
      if (publicRoute || matchedRoute) {
        const routeItem = publicRoute || matchedRoute
        const key = title || routeItem.localKey
        document.title = key ? $t(key) : routeItem.name
      } else {
        document.title = '404'
      }
    }
  }
}

/**
 * 是否为中文
 */
export const isCN = computed(() => {
  return i18n.global.locale.value === langKeyMap.zh
})
